export type AdminCatalogItem = {
  id: number;
  name: string;
  slug: string;
  price: number;
  minimumPrice: number;
  quantity: number;
  description: string;
  testedWith: string;
  codeUrl: string;
  categoryId: number | null;
  category: string;
  sku: string;
  brand: string;
  specSummary: string;
  keywords: string;
  image: string | null;
  images?: Array<{
    id: number | null;
    url: string;
  }>;
  active: boolean;
  totalPurchase: number;
  addedOn: string | null;
};

export type AdminCatalogCategory = {
  id: number;
  name: string;
};

export type AdminCatalogResponse = {
  products: AdminCatalogItem[];
  categories: AdminCatalogCategory[];
  pagination: {
    page: number;
    pageSize: number;
    totalProducts: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  summary: {
    totalProducts: number;
    activeProducts: number;
    archivedProducts: number;
    lowStockProducts: number;
  };
};

export type AdminCatalogInput = {
  name: string;
  slug?: string;
  salePrice: number;
  minimumSalePrice: number;
  quantity: number;
  description: string;
  testedWith?: string;
  codeUrl?: string;
  categoryId?: number | null;
  sku?: string;
  brand?: string;
  specSummary?: string;
  keywords?: string;
  images?: Array<{
    id?: number | null;
    url: string;
  }>;
  active?: boolean;
};

export type AdminCatalogImageUploadResponse = {
  status: "success";
  image: {
    url: string;
    publicId: string | null;
    originalFilename: string;
  };
};

async function parseResponse(response: Response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

export async function fetchAdminCatalog(query: URLSearchParams) {
  const response = await fetch(`/api/admin/catalog?${query.toString()}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminCatalogResponse;
}

export async function createAdminCatalogProduct(input: AdminCatalogInput) {
  const response = await fetch("/api/admin/catalog", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function fetchAdminCatalogProduct(productId: number) {
  const response = await fetch(`/api/admin/catalog/${productId}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminCatalogItem;
}

export async function updateAdminCatalogProduct(productId: number, input: AdminCatalogInput & { active: boolean }) {
  const response = await fetch(`/api/admin/catalog/${productId}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function archiveAdminCatalogProduct(productId: number) {
  const response = await fetch(`/api/admin/catalog/${productId}`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return parseResponse(response);
}

export async function uploadAdminCatalogImage(file: File) {
  const form = new FormData();
  form.set("file", file);

  const response = await fetch("/api/admin/catalog/upload-image", {
    method: "POST",
    credentials: "include",
    body: form
  });

  return (await parseResponse(response)) as AdminCatalogImageUploadResponse;
}

export type AdminCatalogStockLog = {
  id: number;
  addedQuantity: number;
  previousQuantity: number;
  newQuantity: number;
  note: string;
  addedOn: string;
  addedBy: string;
};

export async function addAdminCatalogProductStock(
  productId: number,
  input: { quantity: number; note?: string | null }
) {
  const response = await fetch(`/api/admin/catalog/${productId}/add-stock`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function fetchAdminCatalogProductStockLogs(productId: number) {
  const response = await fetch(`/api/admin/catalog/${productId}/stock-log`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminCatalogStockLog[];
}

export async function downloadAdminCatalogStockExport(filters?: {
  query?: string;
  active?: "all" | "active" | "archived";
}) {
  const params = new URLSearchParams();
  if (filters?.query?.trim()) {
    params.set("q", filters.query.trim());
  }
  if (filters?.active) {
    params.set("active", filters.active);
  }

  const queryString = params.toString() ? `?${params.toString()}` : "";
  const response = await fetch(`/api/admin/catalog/export${queryString}`, {
    credentials: "include"
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `Export failed with status: ${response.status}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const now = new Date().toISOString().split("T")[0];
  link.href = url;
  link.download = `stock_export_${now}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

